import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import useMediaQuery from '@mui/material/useMediaQuery';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import Drawer from '@components/Drawer';
import { Typography } from '@components/Typography';
import { useTranslation, useQueryParams, useConversations, useChannels, useInboxTeams } from '@hooks';
import { AssignedStatus, ConversationStatus, ConversationSorting } from '@constants/conversations';
import { AssignmentType } from '@constants/inbox';
import { CHANNEL } from '@constants/channels';
import { getFullName } from '@helpers/userName';
import { buildAssigneesForView } from '@helpers/inbox';
import { contactsActions, conversationsActions, inboxActions } from '@actions';
import { ReturnIcon } from 'assets/images/icons';
import Dialog from './Dialog';
import ConversationsItemSkeleton from './ConversationsItemSkeleton';
import SelectedDialogsToolbar from './SelectedDialogsToolbar';
import FiltersPanel from '../FiltersPanel';
import SortMenu from '../SortMenu';
import NavigationRail from '../NavigationRail';
import {
    DialogsContainer,
    ConversationsWrapper,
    ScopeHeader,
    ChipsRow,
    StatusToggle,
    ConversationsToolbar,
} from './styles';

const Dialogs = ({
    isMobile,
    filter,
    setFilter,
    scopeHandlers,
    activeConversation,
    setActiveConversation,
    activeConversationRef,
}) => {
    const [selectedConversations, setSelectedConversations] = useState(new Map());
    const [scopeDrawerOpen, setScopeDrawerOpen] = useState(false);

    const language = useSelector(state => state.auth.user?.language);
    const { data, loading, counters, pageToken } = useSelector(state => state.conversations);
    const currentUser = useSelector(state => state.auth.user);
    const users = useSelector(state => state.account?.account?.users || []);
    const agents = useSelector(state => state.agentsLookup?.data || []);
    const { data: conversationTags } = useSelector(state => state.conversationTags);

    const navigate = useNavigate();
    const params = useQueryParams();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { readConversationById } = useConversations();
    const { channels } = useChannels();
    const { teams } = useInboxTeams();
    const isCompact = useMediaQuery('@media (max-width: 1024px)');

    const queryConversationId = params.get('conversationId');

    const defaultAssignees = useMemo(
        () => buildAssigneesForView(filter.assignment, { users, agents, currentUserId: currentUser?.id }),
        [filter.assignment, users, agents, currentUser?.id],
    );

    const assigneesOptions = useMemo(() => {
        const usersOptions = users.map(user => ({
            value: user.id,
            label: getFullName(user.firstName, user.lastName),
            type: AssignmentType.User,
        }));
        const agentsOptions = agents.map(agent => ({
            value: agent.id,
            label: agent.name,
            type: AssignmentType.Agent,
        }));

        const groupedOptions = [];
        groupedOptions.push({ group: t('common.users'), children: usersOptions });

        if (agentsOptions.length > 0) {
            groupedOptions.push({ group: t('Sidebar.agents'), children: agentsOptions });
        }

        groupedOptions.push({
            group: t('AnalyticsReviews.others'),
            children: [{ value: null, label: t('common.unassigned'), type: AssignmentType.Unassigned }],
        });

        return groupedOptions;
    }, [users, agents, t]);

    const scopeTitle = useMemo(() => {
        if (filter.collaboratingOnly) {
            return t('InboxPage.collaborating');
        }
        if (filter.teams?.length === 1) {
            return teams.find(team => team.id === filter.teams[0])?.name || t('filters.teams');
        }
        if (filter.channels?.length === 1) {
            if (filter.channels[0] === CHANNEL.ARCHIVED) {
                return t('common.archived');
            }
            return channels.find(channel => channel.id === filter.channels[0])?.name || t('filters.channels');
        }
        if (filter.tags?.length === 1) {
            return conversationTags.find(tag => tag.id === filter.tags[0])?.name || t('InboxPage.tags');
        }
        if (filter.assignment === AssignedStatus.Mine) {
            return t('InboxPage.myConversations');
        }
        if (filter.assignment === AssignedStatus.Unassigned) {
            return t('InboxPage.unassigned');
        }
        return t('InboxPage.allConversations');
    }, [
        filter.teams,
        filter.channels,
        filter.tags,
        filter.assignment,
        filter.collaboratingOnly,
        teams,
        channels,
        conversationTags,
        t,
    ]);

    useEffect(() => {
        // Rail-driven scope changes replace the filter object; stale selections must not
        // survive a scope switch (bulk actions run against the current filter).
        setSelectedConversations(new Map());
    }, [filter]);

    useEffect(() => {
        if (data.length > 0) {
            /* INFO: when data updated and no selected conversation, select the first one
                if mobile, not need to select the first conversation because it will show always messages screen
            */
            if (!activeConversationRef.current && !isMobile) {
                onSetActiveConversation(data[0]);
                return;
            }
        }
    }, [data]);

    const onSetActiveConversation = conversation => {
        setActiveConversation(conversation);
        if (!conversation?.read) {
            readConversationById(conversation?.id);
        }
        dispatch(inboxActions.getHistory(conversation?.id));
        if (conversation?.contactId !== activeConversationRef.current?.contactId) {
            dispatch(contactsActions.getContact(conversation?.contactId));
        }
    };

    const onSelectConversation = id => {
        setSelectedConversations(prev => {
            const newMap = new Map(prev);
            if (newMap.has(id)) {
                newMap.delete(id);
            } else {
                newMap.set(id, true);
            }
            return newMap;
        });
    };

    const loadMoreConversations = () => {
        if (!pageToken) return;
        dispatch(conversationsActions.loadMore(filter, pageToken));
    };

    const applyFilterValues = newFilterValues => {
        if (queryConversationId) {
            navigate({ search: '' }, { replace: true });
        }
        setFilter(newFilterValues);
        dispatch(conversationsActions.getCounters(newFilterValues));
        dispatch(conversationsActions.get(newFilterValues));
    };

    const onApplyFilters = () => {
        // The selected assignees imply the assignment mode the backend expects; a full
        // selection stays All so secondary filters don't silently narrow the view
        const selectedAssignees = filter.assignees;
        const totalAssigneeOptions = users.length + agents.length + 1;
        let assignment;
        if (selectedAssignees.length >= totalAssigneeOptions) {
            assignment = AssignedStatus.All;
        } else if (selectedAssignees.length === 1 && selectedAssignees[0]?.value === currentUser?.id) {
            assignment = AssignedStatus.Mine;
        } else if (selectedAssignees.length === 1 && selectedAssignees[0]?.value === null) {
            assignment = AssignedStatus.Unassigned;
        } else {
            assignment = AssignedStatus.Assigned;
        }
        applyFilterValues({ ...filter, assignment, conversationId: null });
    };

    const onToggleStatus = status => {
        const isActive = filter.statuses.includes(status);
        if (isActive && filter.statuses.length === 1) {
            return;
        }
        const statuses = isActive ? filter.statuses.filter(s => s !== status) : [...filter.statuses, status];
        applyFilterValues({ ...filter, statuses, conversationId: null });
    };

    const onClearQueryParams = () => {
        navigate({ search: '' }, { replace: true });
        const newFilterValues = { ...filter, conversationId: null };
        setFilter(newFilterValues);
        dispatch(conversationsActions.get(newFilterValues));
    };

    const sortedConversations = useMemo(() => {
        return [...data].sort((a, b) => {
            if (filter.sorting === ConversationSorting.ActivityOldestFirst) {
                return new Date(a.lastActivityAt).getTime() - new Date(b.lastActivityAt).getTime();
            }
            if (filter.sorting === ConversationSorting.CreatedAtNewestFirst) {
                return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            }
            if (filter.sorting === ConversationSorting.CreatedAtOldestFirst) {
                return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            }
            return new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime();
        });
    }, [data, filter.sorting]);

    const allSelected = selectedConversations.has('all');

    return (
        <DialogsContainer>
            <ScopeHeader>
                <Typography
                    variant="subtitle1"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        cursor: isCompact ? 'pointer' : 'default',
                        flex: 1,
                        minWidth: 0,
                    }}
                    onClick={isCompact ? () => setScopeDrawerOpen(true) : undefined}
                >
                    {scopeTitle}
                    {isCompact && <ExpandMoreRoundedIcon sx={{ fontSize: 18 }} />}
                </Typography>
                <SortMenu
                    value={filter.sorting}
                    onChange={sorting => applyFilterValues({ ...filter, sorting, conversationId: null })}
                />
                <FiltersPanel
                    onApplyFilters={onApplyFilters}
                    filter={filter}
                    setFilter={setFilter}
                    assigneesOptions={assigneesOptions}
                    defaultAssignees={defaultAssignees}
                />
            </ScopeHeader>
            <ChipsRow>
                <StatusToggle
                    type="button"
                    active={filter.statuses.includes(ConversationStatus.Open)}
                    onClick={() => onToggleStatus(ConversationStatus.Open)}
                >
                    {t('InboxPage.open')}
                </StatusToggle>
                <StatusToggle
                    type="button"
                    active={filter.statuses.includes(ConversationStatus.Closed)}
                    onClick={() => onToggleStatus(ConversationStatus.Closed)}
                >
                    {t('InboxPage.resolved')}
                </StatusToggle>
            </ChipsRow>
            {isCompact && (
                <Drawer
                    anchor="right"
                    open={scopeDrawerOpen}
                    onClose={() => setScopeDrawerOpen(false)}
                    title={t('Sidebar.messagingInbox')}
                >
                    <NavigationRail
                        variant="drawer"
                        filter={filter}
                        scopeHandlers={scopeHandlers}
                        onItemSelected={() => setScopeDrawerOpen(false)}
                    />
                </Drawer>
            )}
            {selectedConversations.size > 0 ? (
                <SelectedDialogsToolbar
                    selectedConversations={selectedConversations}
                    setSelectedConversations={setSelectedConversations}
                    counters={counters}
                    filter={filter}
                />
            ) : null}
            {filter.conversationId && selectedConversations.size === 0 ? (
                <ConversationsToolbar>
                    <Typography
                        variant="caption"
                        sx={{
                            cursor: 'pointer',
                            color: 'var(--primary-main)',
                            display: 'flex',
                            alignItems: 'center',
                            width: 'fit-content',
                        }}
                        onClick={onClearQueryParams}
                    >
                        <ReturnIcon style={{ marginRight: '8px' }} />
                        <span>{t('InboxPage.backToAllConversations')}</span>
                    </Typography>
                </ConversationsToolbar>
            ) : null}
            <ConversationsWrapper
                id="pl-feed-conversations"
                isEmpty={!loading && data.length === 0}
                hasConversationId={!!filter.conversationId}
            >
                {!loading && data.length === 0 && (
                    <Typography variant="body2">{t('InboxPage.noConversations')}</Typography>
                )}
                {loading && [...Array(5).keys()].map(item => <ConversationsItemSkeleton key={item} />)}
                {!loading && !!data.length && (
                    <InfiniteScroll
                        dataLength={data.length}
                        next={loadMoreConversations}
                        hasMore={!!pageToken}
                        loader={<ConversationsItemSkeleton />}
                        scrollableTarget="pl-feed-conversations"
                    >
                        {sortedConversations.map(conversation => {
                            const isActive = conversation.id === activeConversation?.id;
                            const isSelected =
                                selectedConversations.has(conversation.id) || selectedConversations.has('all');
                            return (
                                <Dialog
                                    key={conversation.id}
                                    data={conversation}
                                    onSetActive={isActive ? null : () => onSetActiveConversation(conversation)}
                                    isActive={isActive}
                                    isSelected={isSelected}
                                    allSelected={allSelected}
                                    onSelect={() => onSelectConversation(conversation.id)}
                                    language={language}
                                    isMobile={isMobile}
                                />
                            );
                        })}
                    </InfiniteScroll>
                )}
            </ConversationsWrapper>
        </DialogsContainer>
    );
};

export default Dialogs;
